// aiConfig.js - AI Provider config + proactive key rotation (budget-aware)
// UPGRADE: proactive switch sebelum rate limit via x-ratelimit-remaining-tokens header

// ── Module-level state ────────────────────────────────────────────────────────
const _keyState       = {};   // { 'groq': 0 }  — index key aktif saat ini
const _rateLimitState = {};   // { 'groq:keyHash': { remaining_tokens, reset_at, ... } }

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseKeys(str) {
  return (str || '').split(/[\n,]/).map(k => k.trim()).filter(Boolean);
}

/** Hash akhir 8 char key sebagai identifier (tidak reversible, tidak expose key) */
function hashKey(k) {
  return k ? k.slice(-8) : 'nokey';
}

/** Parse string reset time dari Groq header: "1.234s", "45ms", "1m30s" → ms */
function parseResetMs(str) {
  if (!str) return 60_000;
  let ms = 0;
  const mMatch  = str.match(/(\d+(?:\.\d+)?)m(?!s)/);
  const sMatch  = str.match(/(\d+(?:\.\d+)?)s/);
  const msMatch = str.match(/(\d+)ms/);
  if (mMatch)  ms += parseFloat(mMatch[1])  * 60_000;
  if (sMatch)  ms += parseFloat(sMatch[1])  * 1_000;
  if (msMatch) ms += parseInt(msMatch[1]);
  return ms || 60_000;
}

/** Extract retry-after dari error message Groq: "Please try again in 4.855s." */
function extractRetryAfterMs(errMsg) {
  const m = errMsg?.match(/try again in (\d+(?:\.\d+)?)s/i);
  return m ? parseFloat(m[1]) * 1_000 : 60_000;
}

/** Estimasi token dari messages (3.5 char/token untuk ID/EN + 100 overhead) */
function estimateTokens(messages = []) {
  const chars = messages.reduce((s, m) =>
    s + (typeof m.content === 'string' ? m.content.length : 0), 0);
  return Math.ceil(chars / 3.5) + 100;
}

/** Simpan info budget dari response headers setelah call berhasil */
function updateBudget(stateKey, headers) {
  if (!headers?.get) return;
  const remTokens   = parseInt(headers.get('x-ratelimit-remaining-tokens')  || '-1');
  const remRequests = parseInt(headers.get('x-ratelimit-remaining-requests') || '-1');
  const limitTokens = parseInt(headers.get('x-ratelimit-limit-tokens')       || '0');
  const resetStr    = headers.get('x-ratelimit-reset-tokens');
  if (remTokens < 0) return; // Header tidak tersedia (CORS tidak expose) — skip
  _rateLimitState[stateKey] = {
    remaining_tokens:   remTokens,
    remaining_requests: remRequests,
    limit_tokens:       limitTokens,
    reset_at:           Date.now() + parseResetMs(resetStr),
    updated_at:         Date.now(),
  };
}

/** Cek apakah key punya cukup budget untuk estimatedTokens (dengan buffer 25%) */
function hasBudgetFor(stateKey, estimatedTokens) {
  const s = _rateLimitState[stateKey];
  if (!s) return true;                            // Belum ada info → asumsikan OK
  if (Date.now() >= s.reset_at) return true;     // Window sudah reset
  return s.remaining_tokens >= Math.ceil(estimatedTokens * 1.25);
}

/** Buat Error dengan status code terlampir */
function httpError(msg, status, retryAfterMs) {
  const e = new Error(msg);
  e.status       = status;
  e.retryAfterMs = retryAfterMs;
  return e;
}

/** Public: ringkasan budget semua key yang diketahui, untuk UI */
export function getKeyBudgets(providerId, apiKeyStr) {
  const keys = parseKeys(apiKeyStr || '');
  return keys.map((key, idx) => {
    const sKey = `${providerId}:${hashKey(key)}`;
    const s    = _rateLimitState[sKey];
    const isReset = !s || Date.now() >= s.reset_at;
    return {
      index:     idx + 1,
      isCurrent: (_keyState[providerId] || 0) === idx,
      remaining: isReset ? null : s.remaining_tokens,
      limit:     isReset ? null : s.limit_tokens,
      pct:       isReset || !s.limit_tokens ? 100 :
                 Math.round(s.remaining_tokens / s.limit_tokens * 100),
      isLow:     !isReset && s && s.limit_tokens > 0 &&
                 (s.remaining_tokens / s.limit_tokens) < 0.2,
    };
  });
}

// ── Core rotation wrapper ─────────────────────────────────────────────────────
/**
 * Wrapper rotasi kunci:
 * 1. PROAKTIF: sebelum call, cek budget tiap key → pilih key dengan budget cukup
 * 2. REAKTIF:  kalau tetap kena 429/401, rotasi ke key berikutnya
 *
 * callFn: async (key: string) => { content, usage, _headers? }
 * messages: digunakan untuk estimasi token (untuk proactive check)
 */
async function callWithRotation(providerId, apiKeyStr, callFn, messages = []) {
  const keys = parseKeys(apiKeyStr);

  // Provider tanpa key — langsung call
  if (keys.length === 0) {
    const result = await callFn('');
    return { ...result, _keyIndex: 1, _keyCount: 1, _proactiveSwitched: false };
  }

  if (_keyState[providerId] === undefined) _keyState[providerId] = 0;

  const estimated = estimateTokens(messages);
  let proactiveSwitched = false;

  // ── PROACTIVE: pilih key terbaik sebelum kirim ────────────────────────────
  if (keys.length > 1) {
    const currentIdx = _keyState[providerId];
    const currentKey = `${providerId}:${hashKey(keys[currentIdx])}`;

    if (!hasBudgetFor(currentKey, estimated)) {
      // Cari key lain yang punya budget cukup
      let switched = false;
      for (let i = 1; i < keys.length; i++) {
        const candidateIdx = (currentIdx + i) % keys.length;
        const candidateKey = `${providerId}:${hashKey(keys[candidateIdx])}`;
        if (hasBudgetFor(candidateKey, estimated)) {
          console.info(
            `[aiConfig] 🔄 Proactive switch: Key #${currentIdx+1} budget rendah` +
            ` (sisa ~${_rateLimitState[currentKey]?.remaining_tokens ?? '?'} token)` +
            ` → Key #${candidateIdx+1}`
          );
          _keyState[providerId] = candidateIdx;
          proactiveSwitched = true;
          switched = true;
          break;
        }
      }
      if (!switched) {
        // Semua key budget rendah → lanjutkan saja (reaktif akan handle)
        console.warn('[aiConfig] Semua key budget rendah, lanjut reaktif.');
      }
    }
  }

  // ── REACTIVE: loop semua key + auto-wait untuk retry-after pendek ──────────
  let lastError;
  let autoWaitCount    = 0;
  const MAX_AUTO_WAIT  = 2;        // max 2x auto-wait per request
  const AUTO_WAIT_CAP  = 10_000;   // hanya auto-wait kalau ≤ 10 detik

  // Loop lebih dari keys.length untuk slot auto-wait retry
  for (let attempt = 0; attempt < keys.length + MAX_AUTO_WAIT; attempt++) {
    // Kalau sudah lewat keys.length, tetap pakai key terakhir (untuk auto-wait retry)
    const keyIdx = Math.min(attempt, keys.length - 1);
    const idx    = (_keyState[providerId] + keyIdx) % keys.length;
    const key    = keys[idx];
    const sKey   = `${providerId}:${hashKey(key)}`;

    try {
      const result = await callFn(key);
      if (result._headers) {
        updateBudget(sKey, result._headers);
        delete result._headers;
      }
      _keyState[providerId] = idx;
      return {
        ...result,
        _keyIndex:          idx + 1,
        _keyCount:          keys.length,
        _proactiveSwitched: proactiveSwitched && attempt === 0,
      };

    } catch (err) {
      const retryMs = err.retryAfterMs || extractRetryAfterMs(err.message);

      // Tandai budget habis untuk key ini
      if (err.status === 429) {
        _rateLimitState[sKey] = {
          ...(_rateLimitState[sKey] || {}),
          remaining_tokens: 0, remaining_requests: 0,
          reset_at: Date.now() + retryMs, updated_at: Date.now(),
        };
      }

      const isLimit = err.status === 429 ||
        /rate.?limit|quota|too many|resource.*exhaust/i.test(err.message);
      const isAuth  = err.status === 401 ||
        /invalid.*key|unauthorized|api.?key.*invalid/i.test(err.message);

      if (isLimit) {
        // Strategi A: Masih ada key lain yang belum dicoba → rotasi
        if (keys.length > 1 && keyIdx < keys.length - 1) {
          const next = (idx + 1) % keys.length;
          _keyState[providerId] = next;
          lastError = new Error(`Key #${idx+1} rate limit → rotasi ke Key #${next+1}`);
          console.warn(`[aiConfig] ${providerId}: ${lastError.message}`);
          continue;
        }
        // Strategi B: Retry-after ≤ 10 detik → tunggu otomatis, tidak perlu user retry manual
        if (autoWaitCount < MAX_AUTO_WAIT && retryMs > 0 && retryMs <= AUTO_WAIT_CAP) {
          autoWaitCount++;
          const waitMs = retryMs + 300; // +300ms safety buffer
          console.info(
            `[aiConfig] ⏳ ${providerId} rate limit — auto-wait ` +
            `${(waitMs/1000).toFixed(1)}s (${autoWaitCount}/${MAX_AUTO_WAIT})`
          );
          await new Promise(r => setTimeout(r, waitMs));
          lastError = new Error(`Auto-wait ${(waitMs/1000).toFixed(1)}s setelah rate limit`);
          continue; // retry key yang sama
        }
      }

      if (isAuth && keys.length > 1) {
        const next = (idx + 1) % keys.length;
        _keyState[providerId] = next;
        lastError = new Error(`Key #${idx+1} invalid → rotasi ke Key #${next+1}`);
        console.warn(`[aiConfig] ${providerId}: ${lastError.message}`);
        continue;
      }

      throw err; // Error lain atau semua opsi habis
    }
  }
  throw new Error(
    `Semua opsi habis (${keys.length} key, ${autoWaitCount}x auto-wait). ` +
    `Terakhir: ${lastError?.message}`
  );
}

// ── Helper: parameter generasi untuk endpoint OpenAI-compatible ───────────────
// (OpenAI, Groq, LM Studio, Generic OpenAI Custom). Semua nilai di-clamp di sini
// sehingga provider tidak perlu mengulang validasi. Seed hanya disertakan bila
// di-set (kosong = biarkan provider acak).
function genParamsOpenAI({ temperature, maxTokens, topP, frequencyPenalty, presencePenalty, seed }) {
  const params = {
    temperature:       clampTemp(temperature),
    max_tokens:        clampMaxTokens(maxTokens),
    top_p:             clampTopP(topP),
    frequency_penalty: clampFreqPen(frequencyPenalty),
    presence_penalty:  clampPresPen(presencePenalty),
  };
  const s = normalizeSeed(seed);
  if (s !== null) params.seed = s;
  return params;
}

// ── Providers ─────────────────────────────────────────────────────────────────
// ── MODEL GROQ BAWAAN — satu rujukan untuk seluruh frontend ────────────────
// Frontend TIDAK bisa membaca env runtime (nilai di-inline saat build), jadi
// ini tidak bisa ikut AI_MODEL_GROQ di ecosystem.config.js secara otomatis.
// ⚠ Bila AI_MODEL_GROQ di backend/ai-service diubah, NILAI INI HARUS DIUBAH
// JUGA lalu frontend di-BUILD ULANG — restart saja tidak cukup. Ketidakcocokan
// tidak menimbulkan error: UI hanya menampilkan model yang berbeda dari yang
// sebenarnya dipakai server.
//
// Latar: llama-3.3-70b-versatile dipensiunkan Groq 16-08-2026 dan tertanam di
// 14 titik lintas backend/frontend; gagalnya muncul sebagai 502 dari API,
// bukan sebagai kesalahan konfigurasi.
export const GROQ_DEFAULT_MODEL = 'openai/gpt-oss-120b';

export const AI_PROVIDERS = {

  openai: {
    name: 'OpenAI', logo: '🟢', requiresKey: true,
    requiresBaseUrl: false,
    docsUrl: 'https://platform.openai.com/api-keys',
    docsLabel: 'OpenAI Platform',
    models: [
      { id: 'gpt-4o-mini',   label: 'GPT-4o Mini (Cepat & Murah)' },
      { id: 'gpt-4o',        label: 'GPT-4o (Premium)'            },
      { id: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo'               },
    ],
    defaultModel: 'gpt-4o-mini',
    fetchModelsFilter: (m) =>
      /gpt-4o-mini|gpt-3\.5|gpt-4\.1-mini|gpt-4\.1-nano|o4-mini/i.test(m.id) &&
      !/embedding|audio|tts|whisper|image|dall/i.test(m.id),
    call: async ({ model, messages, apiKey, baseUrl, temperature, maxTokens, topP, frequencyPenalty, presencePenalty, seed }) => {
      return callWithRotation('openai', apiKey, async (key) => {
        const base = (baseUrl && baseUrl.trim()) || 'https://api.openai.com';
        const res  = await fetch(`${base}/v1/chat/completions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${key}` },
          body: JSON.stringify({ model, messages, ...genParamsOpenAI({ temperature, maxTokens, topP, frequencyPenalty, presencePenalty, seed }) }),
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw httpError(e.error?.message || `OpenAI error ${res.status}`, res.status);
        }
        const data = await res.json();
        return { content: data.choices[0].message.content, usage: data.usage || null, _headers: res.headers };
      }, messages);
    },
  },

  openai_custom: {
    name: 'Generic OpenAI (Custom)', logo: '🔧', requiresKey: true,
    requiresBaseUrl: true,
    docsUrl: '', docsLabel: '',
    keyLabel: 'API Key (kosongkan kalau endpoint tidak butuh)',
    baseUrlPlaceholder: 'https://your-server.com (tanpa /v1)',
    models: [{ id: 'auto', label: 'Auto / Manual (ketik nama model)' }],
    defaultModel: 'auto',
    fetchModelsFilter: () => true,
    call: async ({ model, messages, apiKey, baseUrl, customModel, temperature, maxTokens, topP, frequencyPenalty, presencePenalty, seed }) => {
      if (!baseUrl?.trim()) throw new Error('Base URL wajib diisi untuk Generic OpenAI Custom.');
      const finalModel = (model === 'auto' && customModel) ? customModel : model;
      return callWithRotation('openai_custom', apiKey, async (key) => {
        const headers = { 'Content-Type': 'application/json' };
        if (key) headers['Authorization'] = `Bearer ${key}`;
        const res = await fetch(`${baseUrl.replace(/\/+$/, '')}/v1/chat/completions`, {
          method: 'POST', headers,
          body: JSON.stringify({ model: finalModel, messages, ...genParamsOpenAI({ temperature, maxTokens, topP, frequencyPenalty, presencePenalty, seed }) }),
        });
        if (!res.ok) {
          const txt = await res.text();
          throw httpError('Custom endpoint error: ' + txt.slice(0, 200), res.status);
        }
        const data = await res.json();
        return {
          content: data.choices?.[0]?.message?.content || data.choices?.[0]?.text || '',
          usage: data.usage || null,
          _headers: res.headers,
        };
      }, messages);
    },
  },

  // Slot Generic OpenAI KEDUA. Deskriptornya kembar dengan di atas — yang
  // berbeda hanya id-nya, dan justru id itulah gunanya: `llm_keys` di
  // ai-service dipartisi per string provider, sehingga key kedua gateway tidak
  // saling tertukar saat dirotasi. Tanpa id terpisah, dua gateway di bawah satu
  // provider akan menerima key milik satu sama lain dan gagal 401 dengan pesan
  // yang menyalahkan endpointnya.
  //
  // `name` di sini hanya nilai bawaan; bila `custom_label2` diisi admin, daftar
  // provider menampilkan label itu (lihat providerLabel di AISettings.js).
  openai_custom2: {
    name: 'Generic OpenAI #2', logo: '🔧', requiresKey: true,
    requiresBaseUrl: true,
    docsUrl: '', docsLabel: '',
    keyLabel: 'API Key (kosongkan kalau endpoint tidak butuh)',
    baseUrlPlaceholder: 'https://gateway-kedua.com (tanpa /v1)',
    models: [{ id: 'auto', label: 'Auto / Manual (ketik nama model)' }],
    defaultModel: 'auto',
    fetchModelsFilter: () => true,
    call: async ({ model, messages, apiKey, baseUrl, customModel, temperature, maxTokens, topP, frequencyPenalty, presencePenalty, seed }) => {
      if (!baseUrl?.trim()) throw new Error('Base URL wajib diisi untuk Generic OpenAI #2.');
      const finalModel = (model === 'auto' && customModel) ? customModel : model;
      return callWithRotation('openai_custom2', apiKey, async (key) => {
        const headers = { 'Content-Type': 'application/json' };
        if (key) headers['Authorization'] = `Bearer ${key}`;
        const res = await fetch(`${baseUrl.replace(/\/+$/, '')}/v1/chat/completions`, {
          method: 'POST', headers,
          body: JSON.stringify({ model: finalModel, messages, ...genParamsOpenAI({ temperature, maxTokens, topP, frequencyPenalty, presencePenalty, seed }) }),
        });
        if (!res.ok) {
          const txt = await res.text();
          throw httpError('Custom #2 endpoint error: ' + txt.slice(0, 200), res.status);
        }
        const data = await res.json();
        return {
          content: data.choices?.[0]?.message?.content || data.choices?.[0]?.text || '',
          usage: data.usage || null,
          _headers: res.headers,
        };
      }, messages);
    },
  },

  lmstudio: {
    name: 'LM Studio (Local)', logo: '🖥️', requiresKey: false,
    requiresBaseUrl: true,
    docsUrl: 'https://lmstudio.ai/', docsLabel: 'LM Studio',
    baseUrlPlaceholder: 'http://localhost:1234',
    models: [{ id: 'auto', label: 'Auto / Manual (ketik nama model)' }],
    defaultModel: 'auto',
    fetchModelsFilter: () => true,
    call: async ({ model, messages, baseUrl, customModel, temperature, maxTokens, topP, frequencyPenalty, presencePenalty, seed }) => {
      const base       = (baseUrl?.trim()) || 'http://localhost:1234';
      const finalModel = (model === 'auto' && customModel) ? customModel : model;
      const res = await fetch(`${base.replace(/\/+$/, '')}/v1/chat/completions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: finalModel, messages, ...genParamsOpenAI({ temperature, maxTokens, topP, frequencyPenalty, presencePenalty, seed }) }),
      });
      if (!res.ok) throw new Error(`LM Studio error: ${res.status}. Pastikan server LM Studio berjalan di ${base}.`);
      const data = await res.json();
      return { content: data.choices?.[0]?.message?.content || '', usage: data.usage || null, _keyIndex: 1, _keyCount: 1, _proactiveSwitched: false };
    },
  },

  gemini: {
    name: 'Google Gemini', logo: '🔷', requiresKey: true,
    requiresBaseUrl: false,
    docsUrl: 'https://aistudio.google.com/apikey', docsLabel: 'Google AI Studio',
    keyLabel: 'Gemini API Key (gratis di aistudio.google.com)',
    models: [
      { id: 'gemini-2.0-flash-lite', label: 'Gemini 2.0 Flash Lite (Gratis & Cepat)' },
      { id: 'gemini-2.0-flash',      label: 'Gemini 2.0 Flash (Gratis)'               },
      { id: 'gemini-1.5-flash',      label: 'Gemini 1.5 Flash (Gratis)'               },
      { id: 'gemini-1.5-flash-8b',   label: 'Gemini 1.5 Flash-8B (Gratis, Ringan)'    },
      { id: 'gemini-1.5-pro',        label: 'Gemini 1.5 Pro (Premium)'                },
    ],
    defaultModel: 'gemini-2.0-flash',
    fetchModelsFilter: (id) => /flash|lite/i.test(id) && !/embedding|vision|aqa|imagen/i.test(id),
    call: async ({ model, messages, apiKey, systemPrompt, temperature, maxTokens, topP, frequencyPenalty, presencePenalty, seed }) => {
      return callWithRotation('gemini', apiKey, async (key) => {
        if (!key) throw new Error('Gemini API Key wajib diisi.');

        const sysMsg  = messages.find(m => m.role === 'system');
        const chatMsgs = messages
          .filter(m => m.role !== 'system')
          .map(m => ({ role: m.role === 'assistant' ? 'model' : 'user', parts: [{ text: m.content }] }));

        const deduped = chatMsgs.reduce((acc, cur) => {
          const prev = acc[acc.length - 1];
          if (prev && prev.role === cur.role) {
            prev.parts[0].text += '\n\n' + cur.parts[0].text;
          } else {
            acc.push({ ...cur, parts: [{ text: cur.parts[0].text }] });
          }
          return acc;
        }, []);
        if (deduped.length && deduped[0].role !== 'user') {
          deduped.unshift({ role: 'user', parts: [{ text: '.' }] });
        }

        const body = {
          contents: deduped,
          generationConfig: {
            temperature: clampTemp(temperature),
            topP: clampTopP(topP),
            maxOutputTokens: clampMaxTokens(maxTokens),
            ...(normalizeSeed(seed) !== null ? { seed: normalizeSeed(seed) } : {}),
          },
        };
        const sysText = (sysMsg?.content || systemPrompt || '').trim();
        if (sysText) body.system_instruction = { parts: [{ text: sysText }] };

        const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${encodeURIComponent(key)}`;
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          const msg = e.error?.message || e.error?.status || `Gemini error ${res.status}`;
          throw httpError(msg, res.status);
        }

        const data = await res.json();
        const text = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
        if (!text && data.candidates?.[0]?.finishReason === 'SAFETY') {
          throw new Error('Gemini memblokir respons karena safety filter.');
        }
        const meta = data.usageMetadata || {};
        return {
          content: text,
          usage: {
            prompt_tokens:     meta.promptTokenCount     || 0,
            completion_tokens: meta.candidatesTokenCount || 0,
            total_tokens:      meta.totalTokenCount      || 0,
          },
          _headers: res.headers,
        };
      }, messages);
    },
  },

  groq: {
    name: 'Groq (Cepat & Gratis)', logo: '⚡', requiresKey: true,
    requiresBaseUrl: false,
    docsUrl: 'https://console.groq.com/keys',
    docsLabel: 'Daftar gratis',
    keyLabel: 'Groq API Key (gratis di console.groq.com)',
    // ⚠ Daftar ini HARUS berisi model yang benar-benar masih dilayani Groq.
    // Per 20-08-2026 seluruh isi lama (llama-3.3-70b-versatile,
    // llama-3.1-8b-instant, gemma2-9b-it) sudah TIDAK ADA di /v1/models —
    // dropdown menawarkan model mati ke admin, dan yang pertama bahkan berlabel
    // "Terbaik". Verifikasi sebelum mengubah:
    //   cd /awsbasis/ai-service && node probe-groq-models.js
    models: [
      { id: GROQ_DEFAULT_MODEL,  label: 'GPT-OSS 120B (Terbaik)' },
      { id: 'openai/gpt-oss-20b', label: 'GPT-OSS 20B (Tercepat)' },
      { id: 'qwen/qwen3.6-27b',   label: 'Qwen3.6 27B'            },
    ],
    defaultModel: GROQ_DEFAULT_MODEL,
    fetchModelsFilter: (m) => !/whisper|tts|guard/i.test(m.id),
    call: async ({ model, messages, apiKey, temperature, maxTokens, topP, frequencyPenalty, presencePenalty, seed }) => {
      return callWithRotation('groq', apiKey, async (key) => {
        const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${key}` },
          body: JSON.stringify({ model, messages, ...genParamsOpenAI({ temperature, maxTokens, topP, frequencyPenalty, presencePenalty, seed }) }),
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw httpError(
            e.error?.message || `Groq error ${res.status}`,
            res.status,
            extractRetryAfterMs(e.error?.message)
          );
        }
        const data = await res.json();
        // Groq exposes x-ratelimit-* headers — baca untuk proactive tracking
        return {
          content: data.choices[0].message.content,
          usage:   data.usage || null,
          _headers: res.headers,
        };
      }, messages);
    },
  },

  ollama: {
    name: 'Ollama (Local)', logo: '🦙', requiresKey: false,
    requiresBaseUrl: true,
    docsUrl: 'https://ollama.com/', docsLabel: 'Ollama',
    baseUrlPlaceholder: 'http://localhost:11434',
    models: [
      { id: 'llama3',      label: 'LLaMA 3'     },
      { id: 'mistral',     label: 'Mistral'     },
      { id: 'gemma3',      label: 'Gemma 3'     },
      { id: 'phi3',        label: 'Phi 3'       },
      { id: 'deepseek-r1', label: 'DeepSeek R1' },
      { id: 'custom',      label: 'Custom...'   },
    ],
    defaultModel: 'llama3',
    fetchModelsFilter: () => true,
    call: async ({ model, messages, ollamaUrl = 'http://localhost:11434', customModel, temperature, maxTokens, topP, frequencyPenalty, presencePenalty, seed }) => {
      const finalModel = (model === 'custom' && customModel) ? customModel : model;
      const res = await fetch(`${ollamaUrl}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: finalModel, messages, stream: false, options: {
          temperature: clampTemp(temperature),
          top_p: clampTopP(topP),
          num_predict: clampMaxTokens(maxTokens),
          ...(normalizeSeed(seed) !== null ? { seed: normalizeSeed(seed) } : {}),
        } }),
      });
      if (!res.ok) throw new Error(`Ollama error: ${res.status}. Pastikan Ollama berjalan di ${ollamaUrl}`);
      const data = await res.json();
      return {
        content: data.message?.content || data.response || '',
        usage: data.prompt_eval_count != null
          ? { prompt_tokens: data.prompt_eval_count, completion_tokens: data.eval_count || 0,
              total_tokens: (data.prompt_eval_count || 0) + (data.eval_count || 0) }
          : null,
        _keyIndex: 1, _keyCount: 1, _proactiveSwitched: false,
      };
    },
  },

  anthropic: {
    name: 'Anthropic (Claude)', logo: '🟣', requiresKey: true,
    requiresBaseUrl: false,
    docsUrl: 'https://console.anthropic.com/settings/keys',
    docsLabel: 'Anthropic Console',
    keyLabel: 'API Key (sk-ant-…)',
    // Daftar awal sengaja HANYA satu entri "auto": nama model Claude berganti
    // cukup sering, dan daftar statis apa pun akan basi tanpa ada yang sadar.
    // Tombol "Fetch Models" mengambil daftar sebenarnya dari GET /v1/models —
    // itulah satu-satunya sumber yang tidak pernah ketinggalan.
    models: [{ id: 'auto', label: 'Klik "Fetch Models" untuk memuat daftar' }],
    defaultModel: 'auto',
    fetchModelsFilter: () => true,
    // Tidak ada `call` di sini — sejak semua permintaan lewat AI Service,
    // fungsi call() di file ini tidak pernah dieksekusi lagi. Anthropic
    // ditangani callAnthropic() di ai-service/aiProxy.js.
  },

  bedrock: {
    name: 'AWS Bedrock', logo: '☁️', requiresKey: false, requiresBaseUrl: false,
    isBedrock: true,
    docsUrl: 'https://docs.aws.amazon.com/bedrock/latest/userguide/models-supported.html',
    docsLabel: 'Daftar Model Bedrock',
    models: [
      { id: 'us.anthropic.claude-3-5-sonnet-20241022-v2:0', label: 'Claude 3.5 Sonnet v2' },
      { id: 'us.anthropic.claude-3-5-haiku-20241022-v1:0',  label: 'Claude 3.5 Haiku'    },
      { id: 'us.anthropic.claude-3-haiku-20240307-v1:0',    label: 'Claude 3 Haiku'      },
      { id: 'us.meta.llama3-3-70b-instruct-v1:0',           label: 'Llama 3.3 70B'       },
      { id: 'us.amazon.nova-pro-v1:0',                      label: 'Amazon Nova Pro'     },
      { id: 'us.amazon.nova-lite-v1:0',                     label: 'Amazon Nova Lite'    },
      { id: 'mistral.mistral-large-2407-v1:0',              label: 'Mistral Large'       },
      { id: 'custom',                                       label: 'Custom (ketik Model ID)' },
    ],
    defaultModel: 'us.anthropic.claude-3-5-sonnet-20241022-v2:0',
    fetchModelsFilter: () => true,
    // Bedrock di-proxy lewat backend (/api/ai/bedrock) untuk hindari CORS dan
    // menjaga kredensial tetap di server. Region & credential diambil dari
    // ai_configs di server-side.
    call: async ({ model, messages, customModel, temperature, maxTokens, topP }) => {
      const api = await getApi();
      const finalModel = (model === 'custom' && customModel) ? customModel : model;
      let res;
      try {
        res = await api.post('/api/ai/bedrock', {
          model: finalModel,
          messages,
          temperature: clampTemp(temperature),
          max_tokens:  clampMaxTokens(maxTokens),
          top_p:       clampTopP(topP),
        });
      } catch (e) {
        throw httpError(e.response?.data?.error || e.message || 'Bedrock request gagal.', e.response?.status || 500);
      }
      return {
        content: res.data.content || '',
        usage:   res.data.usage || null,
        _keyIndex: 1, _keyCount: 1, _proactiveSwitched: false,
      };
    },
  },

  // [BEDROCK2] Second Bedrock slot - AWS account #2 (AWS_ACCT2_* on the server),
  // Jakarta region by default. Credentials stay on the server; only the region
  // is configurable here. All calls go through AI Service (no `call` needed).
  bedrock2: {
    name: 'AWS Bedrock #2 (Indomas)', logo: '☁️', requiresKey: false, requiresBaseUrl: false,
    isBedrock: true,
    docsUrl: 'https://docs.aws.amazon.com/bedrock/latest/userguide/models-supported.html',
    docsLabel: 'Bedrock model list',
    models: [
      { id: 'qwen.qwen3-235b-a22b-2507-v1:0',                  label: 'Qwen3 235B A22B (Jakarta native)' },
      { id: 'qwen.qwen3-coder-480b-a35b-v1:0',                 label: 'Qwen3 Coder 480B (Jakarta native)' },
      { id: 'qwen.qwen3-coder-30b-a3b-v1:0',                   label: 'Qwen3 Coder 30B (Jakarta native)' },
      { id: 'qwen.qwen3-32b-v1:0',                             label: 'Qwen3 32B (Jakarta native, 32K context)' },
      { id: 'global.anthropic.claude-sonnet-4-6',              label: 'Claude Sonnet 4.6 (global)' },
      { id: 'global.anthropic.claude-haiku-4-5-20251001-v1:0', label: 'Claude Haiku 4.5 (global)' },
      { id: 'apac.anthropic.claude-sonnet-4-20250514-v1:0',    label: 'Claude Sonnet 4 (APAC)' },
      { id: 'apac.amazon.nova-pro-v1:0',                       label: 'Amazon Nova Pro (APAC)' },
      { id: 'amazon.nova-lite-v1:0',                           label: 'Amazon Nova Lite' },
      { id: 'custom',                                          label: 'Custom (type Model ID)' },
    ],
    defaultModel: 'qwen.qwen3-235b-a22b-2507-v1:0',
    fetchModelsFilter: () => true,
  },
};

export const DEFAULT_SYSTEM_PROMPT = `You are the AI Assistant for Primary Dashboard — an integrated SAP and AWS monitoring system.

You help IT teams and management to:
- Analyse AWS cost data (billing, cost explorer)
- Understand SAP Solution Manager tickets and incidents
- Interpret SAP user utilisation
- Provide optimisation recommendations and troubleshooting

Write in professional English that is easy to follow.
When dashboard data is provided, analyse it specifically and give actionable insight.
Format answers cleanly, using bullet points where helpful.`;

export const STORAGE_KEY = 'ai_config_v3';

async function getApi() {
  const { api } = await import('../../context/AuthContext');
  return api;
}

export async function loadConfigFromServer() {
  try {
    const api = await getApi();
    const res = await api.get('/api/ai/config');
    const { config } = res.data;
    let provider = config.provider || 'groq';
    if (provider === 'anthropic') provider = 'groq';
    const mapped = {
      provider,
      model:        config.model         || (AI_PROVIDERS[provider]?.defaultModel || GROQ_DEFAULT_MODEL),
      apiKey:       config.api_key       || '',
      ollamaUrl:    config.ollama_url    || 'http://localhost:11434',
      baseUrl:      config.base_url      || '',
      customModel:  config.custom_model  || '',
      systemPrompt: config.system_prompt || DEFAULT_SYSTEM_PROMPT,
      temperature:      clampTemp(config.temperature),
      maxTokens:        clampMaxTokens(config.max_tokens),
      topP:             clampTopP(config.top_p),
      frequencyPenalty: clampFreqPen(config.frequency_penalty),
      presencePenalty:  clampPresPen(config.presence_penalty),
      seed:             normalizeSeed(config.seed),
      awsRegion:         config.aws_region || 'us-east-1',
      awsRegion2:        config.aws_region2 || 'ap-southeast-3',
      awsAccessKeyId:    config.aws_access_key_id || '',
      awsSecretAccessKey: config.aws_secret_access_key || '',
      awsSessionToken:   config.aws_session_token || '',
      chartMode:         config.chart_mode === 'manual' ? 'manual' : 'auto',
      // [CODE MODEL] Override mode Coding. '' = ikut provider/model global.
      codeProvider:      config.code_provider || '',
      codeModel:         config.code_model    || '',
      // [CODE BASE URL] Endpoint khusus mode Coding. '' = ikut baseUrl global.
      // Kolom terpisah karena provider Coding boleh berbeda dari global.
      codeBaseUrl:       config.code_base_url || '',
      // [CODE TUNING] Sentinel "ikut env" berbeda per field — 0 untuk yang
      // bilangan bulat, -1 untuk temperature (0 adalah temperature yang SAH).
      codeMaxTokens:     Number(config.code_max_tokens) || 0,
      codeToolMaxIter:   Number(config.code_tool_max_iter) || 0,
      codeTemperature:   Number.isFinite(Number(config.code_temperature))
        ? Number(config.code_temperature) : -1,
      followUp:          config.follow_up !== 0 && config.follow_up !== '0' && config.follow_up !== false,
      hideAccuracy:      config.hide_accuracy === 1 || config.hide_accuracy === '1' || config.hide_accuracy === true,
      // [Phase 2] Key di-mask server-side; pakai flag status, bukan nilai key.
      hasKey:        !!config.has_key,
      keyCount:      config.key_count || 0,
      hasAwsSecret:  !!config.has_aws_secret,
      quickPrompts:  config.quick_prompts || null,   // [A] quick prompts global
      // [CUSTOM2] Slot Generic OpenAI kedua. Field TERPISAH dari baseUrl —
      // keduanya harus bisa hidup bersamaan, itu seluruh alasan slot ini ada.
      // Kalau berbagi satu field, memilih slot 2 lalu Save akan menghapus
      // endpoint slot 1 dan pergantian slot jadi tidak berguna.
      baseUrl2:      config.base_url2      || '',
      customModel2:  config.custom_model2  || '',
      codeBaseUrl2:  config.code_base_url2 || '',
      customLabel:   config.custom_label   || '',
      customLabel2:  config.custom_label2  || '',
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(mapped));
    return mapped;
  } catch (e) {
    console.warn('[aiConfig] Load from server failed:', e.message);
    return loadConfigLocal();
  }
}

export async function saveConfigToServer(config) {
  // [BEDROCK-CRED] Jangan simpan secret AWS di localStorage (bisa terisi oleh
  // autofill browser). Server tetap menerima nilai aslinya di payload bawah.
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    ...config, awsSecretAccessKey: '', awsClearCreds: false,
  }));
  try {
    const api = await getApi();
    await api.post('/api/ai/config', {
      provider:      config.provider,
      model:         config.model,
      api_key:       config.apiKey,
      ollama_url:    config.ollamaUrl,
      base_url:      config.baseUrl,
      custom_model:  config.customModel,
      system_prompt: config.systemPrompt,
      temperature:       clampTemp(config.temperature),
      max_tokens:        clampMaxTokens(config.maxTokens),
      top_p:             clampTopP(config.topP),
      frequency_penalty: clampFreqPen(config.frequencyPenalty),
      presence_penalty:  clampPresPen(config.presencePenalty),
      seed:              normalizeSeed(config.seed),
      aws_region:            config.awsRegion || 'us-east-1',
      aws_region2:           config.awsRegion2 || 'ap-southeast-3',
      aws_access_key_id:     config.awsAccessKeyId || '',
      aws_secret_access_key: config.awsSecretAccessKey || '',
      aws_session_token:     config.awsSessionToken || '',
      // [BEDROCK-CRED] true = admin sengaja menghapus kredensial → server
      // membersihkan akid/secret/token supaya Bedrock pakai IAM role server.
      aws_clear_creds:       config.awsClearCreds === true,
      chart_mode:            config.chartMode === 'manual' ? 'manual' : 'auto',
      code_provider:         config.codeProvider || '',
      code_model:            config.codeModel    || '',
      code_base_url:         config.codeBaseUrl  || '',
      code_max_tokens:       Number(config.codeMaxTokens)   || 0,
      code_tool_max_iter:    Number(config.codeToolMaxIter) || 0,
      code_temperature:      Number.isFinite(Number(config.codeTemperature))
        ? Number(config.codeTemperature) : -1,
      follow_up:             (config.followUp === false) ? 0 : 1,
      hide_accuracy:         config.hideAccuracy ? 1 : 0,
      quick_prompts:         config.quickPrompts || null,   // [A] quick prompts global
      // [CUSTOM2] Selalu dikirim, termasuk saat slot 2 tidak aktif. Mengirimnya
      // hanya saat aktif akan membuat server menerima undefined lalu menulis ''
      // ke kolomnya — endpoint slot 2 terhapus setiap kali admin menyimpan
      // konfigurasi dengan slot 1 aktif, dan gejalanya baru terasa nanti.
      base_url2:             config.baseUrl2      || '',
      custom_model2:         config.customModel2  || '',
      code_base_url2:        config.codeBaseUrl2  || '',
      custom_label:          config.customLabel   || '',
      custom_label2:         config.customLabel2  || '',
    });
    return true;
  } catch (e) {
    console.error('[aiConfig] Gagal simpan ke server:', e.response?.data || e.message);
    throw e;
  }
}

export function loadConfigLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const cfg = JSON.parse(raw);
    if (cfg.provider === 'anthropic') { cfg.provider = 'groq'; cfg.model = GROQ_DEFAULT_MODEL; }
    return cfg;
  } catch { return null; }
}

export const loadConfig = loadConfigLocal;
export const saveConfig = (config) => { localStorage.setItem(STORAGE_KEY, JSON.stringify(config)); };

// Kunci dashboard untuk mode Coding.
//
// Didefinisikan DI SINI, bukan di AIChat.js, karena AISettings.js juga
// membutuhkannya — sementara AIChat.js meng-import AISettings.js. Menaruhnya di
// AIChat berarti AISettings harus meng-import balik dari AIChat, dan itu impor
// melingkar. Pola yang sama dipakai routesMeta.js: nilai bersama pindah ke file
// data murni yang tidak meng-import siapa pun.
export const CODE_KEY = '__code__';

export function getDefaultConfig() {
  return {
    provider: 'groq', model: GROQ_DEFAULT_MODEL,
    apiKey: '', ollamaUrl: 'http://localhost:11434',
    baseUrl: '', customModel: '', systemPrompt: DEFAULT_SYSTEM_PROMPT,
    temperature: 0.2, maxTokens: 2000, topP: 1.0,
    frequencyPenalty: 0, presencePenalty: 0, seed: null,
    chartMode: 'auto',
    codeProvider: '', codeModel: '', codeBaseUrl: '',
    baseUrl2: '', customModel2: '', codeBaseUrl2: '',
    customLabel: '', customLabel2: '',
    codeMaxTokens: 0, codeToolMaxIter: 0, codeTemperature: -1,
    followUp: true,
    hideAccuracy: false,
    awsRegion: 'us-east-1', awsAccessKeyId: '', awsSecretAccessKey: '', awsSessionToken: '',
    awsRegion2: 'ap-southeast-3',
  };
}

// Default & batas parameter generasi (precision-oriented, sejajar dgn server)
export const DEFAULT_TEMPERATURE = 0.2;
export const DEFAULT_MAX_TOKENS  = 2000;
export const DEFAULT_TOP_P       = 1.0;
export const DEFAULT_FREQ_PEN    = 0;
export const DEFAULT_PRES_PEN    = 0;
function clampNum(v, lo, hi, def) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : def;
}
export function clampTemp(v)      { return clampNum(v, 0, 2, DEFAULT_TEMPERATURE); }
export function clampMaxTokens(v) { return Math.round(clampNum(v, 100, 8000, DEFAULT_MAX_TOKENS)); }
export function clampTopP(v)      { return clampNum(v, 0, 1, DEFAULT_TOP_P); }
export function clampFreqPen(v)   { return clampNum(v, -2, 2, DEFAULT_FREQ_PEN); }
export function clampPresPen(v)   { return clampNum(v, -2, 2, DEFAULT_PRES_PEN); }
export function normalizeSeed(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
}

export function getKeyCount(apiKeyStr) {
  return parseKeys(apiKeyStr).length;
}

export async function fetchModelsFromProvider({ provider, apiKey, baseUrl, ollamaUrl, awsRegion }) {
  const api = await getApi();
  const res = await api.post('/api/ai/fetch-models', {
    provider,
    api_key:    apiKey   || '',
    base_url:   baseUrl  || '',
    ollama_url: ollamaUrl || '',
    aws_region: awsRegion || '',
  });
  return res.data.models || [];
}

// ── Per-dashboard config (system_prompt + quick_prompts) ─────────────────────
// system_prompt & quick_prompts disimpan per user+dashboard. Provider/model/key
// tetap shared (loadConfigFromServer/saveConfigToServer).
export async function loadDashboardConfig(dashboard) {
  if (!dashboard) return { system_prompt: '', quick_prompts: null };
  try {
    const api = await getApi();
    const res = await api.get('/api/ai/dashboard-config', { params: { dashboard } });
    return res.data.config || { system_prompt: '', quick_prompts: null };
  } catch (e) {
    console.warn('[aiConfig] loadDashboardConfig failed:', e.message);
    return { system_prompt: '', quick_prompts: null };
  }
}

export async function saveDashboardConfig(dashboard, { system_prompt = '', quick_prompts = null } = {}) {
  const api = await getApi();
  await api.post('/api/ai/dashboard-config', { dashboard, system_prompt, quick_prompts });
}
